import { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import type { PocGroup } from "../data-model/pocAssignmentModel";

export const TABLE_NAME = "poc-config-table";
export const ROWS_PER_PAGE = 10;

/** Maximum batch tags shown in the POC configuration table; extra batches use a "+N more" overflow. */
export const MAX_VISIBLE_BATCH_TAGS = 4;

/** Firestore doc id under the pharmacy_order_updates/metadata collection. */
export const POC_CONFIG_TABLE_DOC_ID = "poc_config_table";

export const AVAILABILITY_AVAILABLE = "available";
export const AVAILABILITY_NOT_AVAILABLE = "not_available";

export const GROUP_BADGE_LIST: BadgeWithIconProps[] = [
  { id: "G1", text: "G1", bgColor: "#f5f5f5", textColor: "#333333" },
  { id: "G2", text: "G2", bgColor: "#f5f5f5", textColor: "#333333" },
  { id: "G3", text: "G3", bgColor: "#f5f5f5", textColor: "#333333" },
];

const GROUP_DEFAULT_STYLE = { bgColor: "#f5f5f5", textColor: "#333333" };

export const AVAILABILITY_BADGE_LIST: BadgeWithIconProps[] = [
  {
    id: AVAILABILITY_AVAILABLE,
    text: "Available",
    bgColor: "#eefcf4",
    textColor: "#003d15",
  },
  {
    id: AVAILABILITY_NOT_AVAILABLE,
    text: "Not Available",
    bgColor: "#fde8e8",
    textColor: "#8b0000",
  },
];

/**
 * Canonical cohort options for the POC config table cohort column and the
 * Add New User modal. Legacy row values (e.g. green/yellow/red) still appear
 * when present in loaded data via `buildCohortBadgeList` extras.
 */
export const COHORT_OPTION_VALUES = ["All", "Orange", "Green", "Unknown"] as const;

/**
 * Drug-cohort values written to poc_assignment rules when a table row uses cohort
 * "All" (one rule per target, with the same batches and POCs).
 */
export const ALL_COHORT_EXPAND_TARGETS = ["Green", "Orange", "Unknown"] as const;

/**
 * Returns true when the cohort value means "apply to every drug cohort group".
 *
 * @param cohort - Cohort string from the POC config table row.
 * @returns True when cohort is "All" (case-insensitive).
 */
export const isAllCohort = (cohort: string): boolean =>
  cohort.trim().toLowerCase() === "all";

const COHORT_COLOR_MAP: Record<string, { bgColor: string; textColor: string }> =
  {
    all: { bgColor: "#eef2ff", textColor: "#1e3a8a" },
    green: { bgColor: "#eefcf4", textColor: "#003d15" },
    yellow: { bgColor: "#fffbeb", textColor: "#8f3200" },
    red: { bgColor: "#fde8e8", textColor: "#8b0000" },
    orange: { bgColor: "#fff4e6", textColor: "#8f3200" },
    unknown: { bgColor: "#f5f5f5", textColor: "#333333" },
  };

const COHORT_DEFAULT_STYLE = { bgColor: "#f5f5f5", textColor: "#333333" };

/**
 * Builds a cohort badge for a single value, colouring known cohort names.
 *
 * @param value - Cohort string; colours match known keys case-insensitively (e.g. "Green", "orange").
 * @returns Badge descriptor for the cohort cell/dropdown.
 */
export const buildCohortBadge = (value: string): BadgeWithIconProps => {
  const style = COHORT_COLOR_MAP[value.toLowerCase()] ?? COHORT_DEFAULT_STYLE;
  return { id: value, text: value, ...style };
};

/**
 * Builds the cohort dropdown options from the default set plus any extra
 * values already present in the data.
 *
 * @param extraValues - Cohort values seen in the loaded rows.
 * @returns Distinct cohort badge options.
 */
export const buildCohortBadgeList = (
  extraValues: string[],
): BadgeWithIconProps[] => {
  const distinct = [
    ...new Set([...COHORT_OPTION_VALUES, ...extraValues].filter(Boolean)),
  ];
  return distinct.map(buildCohortBadge);
};

/**
 * Builds the group dropdown options from defaults plus any extra values
 * already present in the data or added via the Groups modal.
 *
 * @param extraValues - Group values seen in loaded rows or added in-session.
 * @returns Distinct group badge options.
 */
export const buildGroupBadgeList = (
  extraValues: string[],
): BadgeWithIconProps[] => {
  const distinct = Array.from(
    new Set(
      [...GROUP_BADGE_LIST.map((badge) => badge.id), ...extraValues].filter(
        Boolean,
      ),
    ),
  );
  return distinct.map((id) => ({ id, text: id, ...GROUP_DEFAULT_STYLE }));
};

/**
 * Builds group badge options from an explicit name list only (e.g. Firestore
 * `poc_assignment_groups` entries), without injecting default G1–G3.
 *
 * @param groupNames - Raw group names (trimmed, deduped).
 * @returns Badge list for selects.
 */
export const buildGroupBadgeListFromNames = (
  groupNames: string[],
): BadgeWithIconProps[] => {
  const distinct = Array.from(
    new Set(groupNames.map((n) => n.trim()).filter(Boolean)),
  );
  return distinct.map((id) => ({ id, text: id, ...GROUP_DEFAULT_STYLE }));
};

export const isPocGroup = (value: string): value is PocGroup =>
  GROUP_BADGE_LIST.some((badge) => badge.id === value);

export const isAvailabilityId = (value: string): boolean =>
  value === AVAILABILITY_AVAILABLE || value === AVAILABILITY_NOT_AVAILABLE;

/**
 * Normalizes a stored availability value to a boolean.
 * Handles boolean fields and legacy string ids (`available` / `not_available`).
 *
 * @param value - Raw availability from Firestore or form state.
 * @returns True when the POC is available for assignment rules.
 */
export const normalizeAvailability = (value: unknown): boolean => {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === AVAILABILITY_AVAILABLE) return true;
    if (normalized === AVAILABILITY_NOT_AVAILABLE) return false;
  }
  return false;
};

/** Maps the boolean availability field to its badge id. */
export const availabilityToBadgeId = (available: boolean): string =>
  available ? AVAILABILITY_AVAILABLE : AVAILABILITY_NOT_AVAILABLE;
